/**
 * ScopeDrawPage — route wrapper for the drawing-path SCOPE flow.
 *
 * Days 2-4:
 *   - Toolbar (Pan / Calibrate scale / Draw room) + Undo/Clear
 *   - Calibration dialog (enter real-world metres for the drawn line)
 *   - Polygon drawing with snap-close, Escape-cancel, live area/perimeter
 *   - Room dialog (name + finishes + doors/windows)
 *   - Multi-floor tabs (Ground / First / Second / Basement)
 *   - Room list sidebar (edit/delete per room)
 *   - Debounced 1.2s autosave of EVERYTHING incl. in-progress polygon,
 *     so a refresh mid-draw preserves the work.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { SiteNav } from './SiteNav';
import { useAuth } from '../contexts/AuthContext';
import { DrawingCanvas } from './scope/DrawingCanvas';
import { WalkthroughTip } from './scope/WalkthroughTip';
import { ScopeAIIntro } from './scope/ScopeAIIntro';
import ScopeAIReview from './scope/ScopeAIReview';
import { polygonAreaPx, polygonPerimeterPx, pxAreaToM2, pxLenToM, dist } from './scope/geometry';
import { trackEvent } from '../lib/analytics';
import { QSCallCTA } from './QSCallCTA';
import {
  Upload, ArrowLeft, Move, Ruler, Pencil, Undo2, Trash2, Plus, Check, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLOOR_OPTIONS = [
  { id: 'gf', label: 'Ground Floor' },
  { id: '1f', label: 'First Floor' },
  { id: '2f', label: 'Second Floor' },
  { id: 'b',  label: 'Basement' },
];

// Per-surface finish options — what homeowners actually answer.
// Internal spec levels (basic/mid/high) are derived from these answers.
const WALL_FINISH_OPTIONS = [
  { id: 'strip_skim_paint', label: 'Strip wallpaper, skim and paint', hint: 'Full preparation' },
  { id: 'skim_paint',       label: 'Skim and paint',                  hint: 'Walls sound, need skim' },
  { id: 'paint',            label: 'Paint only',                      hint: 'Walls in good condition' },
  { id: 'tile',             label: 'Tile (wet room or splashback)',   hint: 'Kitchen / bathroom' },
  { id: 'keep',             label: 'Keep existing',                   hint: 'No work' },
];
const FLOOR_FINISH_OPTIONS = [
  { id: 'carpet',     label: 'Carpet and underlay' },
  { id: 'engineered', label: 'Engineered wood' },
  { id: 'lvt',        label: 'LVT (luxury vinyl tile)' },
  { id: 'tile',       label: 'Ceramic or porcelain tile' },
  { id: 'laminate',   label: 'Laminate' },
  { id: 'keep',       label: 'Keep existing' },
];
const CEILING_FINISH_OPTIONS = [
  { id: 'paint',            label: 'Paint only' },
  { id: 'skim_paint',       label: 'Skim and paint' },
  { id: 'new_board_skim',   label: 'New plasterboard, skim and paint' },
  { id: 'keep',             label: 'Artex — leave' },
];

// Derive internal spec level from the chosen finishes.
// Used only to initialise the per-room `finish` field on create for legacy
// engine paths that still inspect it.
function deriveFinishLevel(wall, floor, ceiling) {
  const highWall = wall === 'strip_skim_paint';
  const highFloor = floor === 'engineered' || floor === 'tile';
  const highCeil = ceiling === 'new_board_skim';
  if (highWall || highFloor || highCeil) return 'high';
  const basicWall = wall === 'paint' || wall === 'keep';
  const basicFloor = floor === 'laminate' || floor === 'carpet' || floor === 'keep';
  const basicCeil = ceiling === 'paint' || ceiling === 'keep';
  if (basicWall && basicFloor && basicCeil) return 'basic';
  return 'mid';
}

// Legacy spec levels (kept so existing drafts still load without crashing)
const FINISH_LEVELS = [
  { id: 'basic', label: 'Basic' },
  { id: 'mid',   label: 'Mid-spec' },
  { id: 'high',  label: 'High-spec' },
];

// Bathroom spec tiers (new — mirrors kitchen)
const BATHROOM_SPEC_OPTIONS = [
  { id: 'budget', label: 'Basic', desc: 'White suite, standard tiling' },
  { id: 'mid',    label: 'Mid-range', desc: 'Mid-range suite, full tiling, heated towel rail' },
  { id: 'high',   label: 'High-end', desc: 'Premium suite, feature tiling, underfloor heating' },
];

function emptyFloor(id, label) {
  return {
    id, label,
    plan_image_url: null,
    image_width: 0,
    image_height: 0,
    scale: null,              // { p1:[x,y], p2:[x,y], metres, factor_m_per_px }
    rooms: [],                // [{ id, name, polygon_px, area_m2, perimeter_m, finish, doors, windows }]
    current_polygon_px: [],   // in-progress room (autosaved)
  };
}

function recalcRoomMeasures(room, scale) {
  const areaPx = polygonAreaPx(room.polygon_px || []);
  const perimPx = polygonPerimeterPx(room.polygon_px || []);
  return {
    ...room,
    area_m2: scale?.factor_m_per_px ? pxAreaToM2(areaPx, scale.factor_m_per_px) : null,
    perimeter_m: scale?.factor_m_per_px ? pxLenToM(perimPx, scale.factor_m_per_px) : null,
  };
}

export const ScopeDrawPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [draftId, setDraftId] = useState(params.get('draft') || null);
  const [floorsMap, setFloorsMap] = useState({});
  const [activeFloorId, setActiveFloorId] = useState('gf');
  const [tool, setTool] = useState('pan');           // 'pan' | 'calibrate' | 'draw'
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [ensuring, setEnsuring] = useState(true);
  const [calibDialog, setCalibDialog] = useState(null);  // { p1, p2, pxLen }
  const [roomDialog, setRoomDialog] = useState(null);    // { polygon_px }
  const [addFloorOpen, setAddFloorOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'

  // Purchase-step state (second-half of the page experience)
  const [phase, setPhase] = useState(() => params.get('phase') === 'purchase' ? 'purchase' : 'draw');  // 'draw' | 'ai-review' | 'purchase'
  const [preview, setPreview] = useState(null);  // { total_low, total_high, region, location_factor, total_area_m2, rooms_count }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [addressForm, setAddressForm] = useState({ address: '', postcode: '' });
  // §3.6 / §3.9 — property_type + scale_of_works captured once at Indicative stage, carried through SCOPE.
  // Pre-fill from URL params when user arrives via "Refine into SCOPE" button.
  const [projectInfo, setProjectInfo] = useState({
    address: params.get('address') || '',
    postcode: params.get('postcode') || '',
    property_type: params.get('property_type') || '',
    scale_of_works: params.get('scale_of_works') || '',
  });
  const [projectInfoConfirmed, setProjectInfoConfirmed] = useState(false);
  // Mirror pre-filled address into the legacy addressForm used by the preview/purchase flow.
  useEffect(() => {
    if (projectInfo.address || projectInfo.postcode) {
      setAddressForm((prev) => ({
        address: prev.address || projectInfo.address,
        postcode: prev.postcode || projectInfo.postcode,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [checkoutTier, setCheckoutTier] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [billing, setBilling] = useState(null);  // { tier, monthly_allowances, ... }

  // Load billing state when we enter purchase phase so we can show redeem CTA for Pro/Pro+
  useEffect(() => {
    if (phase !== 'purchase' || !user) return;
    axios.get(`${API}/billing/me`)
      .then((r) => setBilling(r.data))
      .catch(() => setBilling(null));
  }, [phase, user]);

  // First-use walkthrough ─────────────────────────────────────────────
  // Step 1: point at upload zone; Step 2: point at "Set scale"; Step 3: point at "Draw room"
  // Shown once per account on first visit (tracked via user.flags.scope_draw_walkthrough_seen)
  // and only on genuinely fresh sessions (no rooms yet, no scale yet).
  const [walkStep, setWalkStep] = useState(0);  // 0 = not started, 1/2/3 = active step, 99 = done
  const uploadTargetRef = useRef(null);
  const [calibTargetEl, setCalibTargetEl] = useState(null);
  const [drawTargetEl, setDrawTargetEl] = useState(null);

  const persistWalkSeen = useCallback(async (completed = false) => {
    try {
      await axios.post(`${API}/auth/flags`, { key: 'scope_draw_walkthrough_seen', value: true });
      if (completed) {
        await axios.post(`${API}/auth/flags`, { key: 'scope_draw_walkthrough_completed', value: true });
      }
    } catch (err) {
      // Best-effort — dismissing locally is enough for this session, but log so
      // persistent failures are visible in the browser console.
      console.warn('Walkthrough flag persist failed:', err?.message);
    }
  }, []);
  const dismissWalk = useCallback(() => {
    setWalkStep(99);
    persistWalkSeen(false);
    trackEvent('walkthrough_dismissed', {
      path: 'draw', draft_id: draftId,
      properties: { dismissed_at_step: walkStepRef.current, completed: false },
    });
  }, [persistWalkSeen, draftId]);
  const completeWalk = useCallback(() => {
    setWalkStep(99);
    persistWalkSeen(true);
    trackEvent('walkthrough_dismissed', {
      path: 'draw', draft_id: draftId,
      properties: { dismissed_at_step: 3, completed: true },
    });
  }, [persistWalkSeen, draftId]);
  // Track the current walk step via ref so dismissal callback always has the latest value
  const walkStepRef = useRef(0);

  // Gate the walkthrough effect until we've finished loading the draft
  // (so we don't accidentally show Step 1 before resolving whether the
  // user is actually resuming an existing session).
  const [draftLoaded, setDraftLoaded] = useState(false);

  // ── SCOPEai intro — shown once per user-session before the canvas ──
  const [showIntro, setShowIntro] = useState(false);
  const [introSeen, setIntroSeen] = useState(() => {
    try { return localStorage.getItem('sow.scope_ai_intro_seen') === '1'; } catch { return false; }
  });
  useEffect(() => {
    // Show the intro only when: draft is loaded, no plan uploaded yet, user
    // hasn't seen it before this session.
    if (!draftLoaded || introSeen) return;
    const hasAnyPlan = Object.values(floorsMap).some(f => !!f.plan_image_url);
    const hasAnyRoom = Object.values(floorsMap).some(f => (f.rooms || []).length > 0);
    if (!hasAnyPlan && !hasAnyRoom) setShowIntro(true);
  }, [draftLoaded, introSeen, floorsMap]);
  const markIntroSeen = () => {
    setShowIntro(false);
    setIntroSeen(true);
    try { localStorage.setItem('sow.scope_ai_intro_seen', '1'); } catch { /* no-op */ }
  };

  const floor = floorsMap[activeFloorId] || null;

  // If we landed here with ?phase=purchase (from the ScopeChat finalise step),
  // kick off the calculation immediately so the purchase card can render.
  const purchasePrimedRef = useRef(false);
  useEffect(() => {
    if (phase !== 'purchase' || !draftLoaded || !draftId || purchasePrimedRef.current) return;
    purchasePrimedRef.current = true;
    (async () => {
      setPreviewLoading(true);
      setPreviewError('');
      try {
        const res = await axios.post(`${API}/scope/drafts/${draftId}/calculate`);
        setPreview(res.data);
      } catch (err) {
        setPreviewError(err.response?.data?.error || 'Could not price your project. Please try again.');
      } finally {
        setPreviewLoading(false);
      }
    })();
  }, [phase, draftLoaded, draftId]);

  // Require auth
  useEffect(() => {
    if (user === false) navigate('/login?next=/scope/draw', { replace: true });
  }, [user, navigate]);

  // Analytics: fire scope_path_viewed once per mount (path = 'draw')
  useEffect(() => {
    trackEvent('scope_path_viewed', { path: 'draw', properties: { path_type: 'draw' } });
  }, []);

  // ── Walkthrough orchestrator ─────────────────────────────────────
  // Step 1: show when draft loaded & no plan uploaded yet & user hasn't seen it
  const userFlags = user?.flags || {};
  useEffect(() => {
    walkStepRef.current = walkStep;
  }, [walkStep]);
  useEffect(() => {
    if (!user || ensuring || !draftLoaded) return;
    if (userFlags.scope_draw_walkthrough_seen) return;
    const hasUploadedPlan = Object.values(floorsMap).some(f => f?.plan_image_url);
    const hasScale = Object.values(floorsMap).some(f => f?.scale);
    const hasRooms = Object.values(floorsMap).some(f => (f?.rooms || []).length > 0);
    // Resumed session detection — only when first loading (walkStep === 0).
    // If the user already has a scale or rooms, they've been here before → skip.
    if (walkStep === 0 && (hasScale || hasRooms)) { setWalkStep(99); return; }
    if (walkStep === 0 && !hasUploadedPlan) {
      setWalkStep(1);
      trackEvent('walkthrough_started', { path: 'draw', draft_id: draftId, properties: { total_steps: 3 } });
    }
    if (walkStep === 1 && hasUploadedPlan)  setWalkStep(2);
    if (walkStep === 2 && hasScale)         setWalkStep(3);
    if (walkStep === 3 && hasRooms)         { completeWalk(); }
  }, [user, ensuring, draftLoaded, floorsMap, userFlags.scope_draw_walkthrough_seen, walkStep, completeWalk, draftId]);

  // Ensure a draft exists for this drawing session
  // Strategy: if the user has an existing draw-method draft, resume it.
  // Otherwise POST a new draft (which respects the free-tier limit).
  useEffect(() => {
    if (!user || draftId) { setEnsuring(false); return; }
    (async () => {
      try {
        const list = await axios.get(`${API}/scope/drafts`);
        const drafts = list.data?.drafts || [];
        const existingDraw = drafts.find((d) => d.method === 'draw');
        if (existingDraw) {
          setDraftId(existingDraw.draft_id);
          return;
        }
        const res = await axios.post(`${API}/scope/drafts`, { scope_level: 'detailed', method: 'draw' });
        const id = res.data?.draft?.draft_id;
        if (id) setDraftId(id);
      } catch (err) {
        if (err.response?.status === 402) {
          setError('You have reached your free-tier project limit. Archive or delete an existing project to start a new one.');
        } else {
          setError('Could not start a drawing session. Please try again.');
        }
      } finally {
        setEnsuring(false);
      }
    })();
  }, [user, draftId]);

  // Load existing draft state if we have a draft_id
  useEffect(() => {
    if (!user || !draftId) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/scope/drafts/${draftId}`);
        const d = res.data || {};
        if (d.floors_map && typeof d.floors_map === 'object') {
          setFloorsMap(d.floors_map);
        }
        if (d.active_floor_id) setActiveFloorId(d.active_floor_id);
        if (d.address || d.postcode) {
          setAddressForm({ address: d.address || '', postcode: d.postcode || '' });
        }
        // §3.9 — restore pre-filled Project Info so a resumed draft stays confirmed.
        if (d.property_type || d.scale_of_works || d.address) {
          setProjectInfo({
            address: d.address || '',
            postcode: d.postcode || '',
            property_type: d.property_type || '',
            scale_of_works: d.scale_of_works || '',
          });
          // If draft already has any meaningful project info, skip the confirm card.
          setProjectInfoConfirmed(true);
        }
      } catch {
        // new draft — no data to load, fine
      } finally {
        setDraftLoaded(true);
      }
    })();
  }, [user, draftId]);

  // ── Debounced autosave (1.2s) ─────────────────────────────────────────
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!draftId || !user) return;
    if (Object.keys(floorsMap).length === 0) return;
    setSaveStatus('saving');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await axios.post(`${API}/scope/drafts`, {
          draft_id: draftId,
          scope_level: 'detailed',
          method: 'draw',
          floors_map: floorsMap,
          active_floor_id: activeFloorId,
          // §3.6 / §3.9 — persist so a resumed draft stays pre-filled
          address: projectInfo.address || undefined,
          postcode: projectInfo.postcode || undefined,
          property_type: projectInfo.property_type || undefined,
          scale_of_works: projectInfo.scale_of_works || undefined,
        });
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 1200);
      } catch {
        setSaveStatus('idle');
      }
    }, 1200);
    return () => clearTimeout(saveTimer.current);
  }, [floorsMap, activeFloorId, draftId, user, projectInfo]);

  // ── Escape cancels in-progress room ───────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (calibDialog || roomDialog || addFloorOpen) return;  // let dialogs handle their own Esc
        if (tool === 'draw' && floor?.current_polygon_px?.length) {
          updateFloor(activeFloorId, (f) => ({ ...f, current_polygon_px: [] }));
        }
        if (tool === 'calibrate') {
          // handled inside DrawingCanvas resetting pendingCalib via mode change
          setTool('pan');
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tool, floor, activeFloorId, calibDialog, roomDialog, addFloorOpen]);  // eslint-disable-line

  // Tool helpers
  const updateFloor = useCallback((fid, updater) => {
    setFloorsMap((prev) => {
      const current = prev[fid] || emptyFloor(fid, FLOOR_OPTIONS.find(f => f.id === fid)?.label || fid);
      return { ...prev, [fid]: updater(current) };
    });
  }, []);

  // ── Plan upload ───────────────────────────────────────────────────────
  const handleUpload = useCallback(async (e, forFloorId) => {
    const file = e.target.files?.[0];
    if (!file || !draftId) return;
    setError('');
    setUploading(true);
    // Detect whether this floor already had a plan (→ reupload event)
    const targetFloorId = forFloorId || activeFloorId;
    const hadPlanAlready = !!floorsMap[targetFloorId]?.plan_image_url;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('draft_id', draftId);
      fd.append('floor_id', forFloorId || activeFloorId);
      const res = await axios.post(`${API}/scope/plans/upload`, fd);
      const fid = res.data.floor_id;
      const label = FLOOR_OPTIONS.find(f => f.id === fid)?.label || fid;
      updateFloor(fid, (f) => ({
        ...f,
        id: fid,
        label,
        plan_image_url: res.data.url,
        image_width: res.data.width,
        image_height: res.data.height,
      }));
      setActiveFloorId(fid);
      setAddFloorOpen(false);
      trackEvent(hadPlanAlready ? 'scope_plan_reuploaded' : 'scope_plan_uploaded', {
        path: 'draw', draft_id: draftId,
        properties: { floor_id: fid, file_type: file.type || '', file_size: file.size || 0 },
      });
    } catch (err) {
      const msg = err.response?.data?.error || 'Upload failed';
      setError(msg);
    } finally {
      setUploading(false);
      // reset input so the same file can be selected again if needed
      try { e.target.value = ''; } catch {}  // eslint-disable-line no-empty
    }
  }, [draftId, activeFloorId, updateFloor, floorsMap]);

  // ── Calibration ───────────────────────────────────────────────────────
  const handleCalibratePoints = useCallback(([p1, p2]) => {
    const pxLen = dist(p1, p2);
    setCalibDialog({ p1, p2, pxLen });
  }, []);

  const commitCalibration = (metres) => {
    if (!calibDialog || !metres || metres <= 0) return;
    const factor = metres / calibDialog.pxLen;  // metres per pixel
    const newScale = { p1: calibDialog.p1, p2: calibDialog.p2, metres, factor_m_per_px: factor };
    const hadPrevScale = !!floor?.scale;
    const prevRoomCount = (floor?.rooms || []).length;
    updateFloor(activeFloorId, (f) => {
      // Recompute area/perimeter for every existing room (non-destructive to polygons)
      const rooms = (f.rooms || []).map((r) => recalcRoomMeasures(r, newScale));
      return { ...f, scale: newScale, rooms };
    });
    setCalibDialog(null);
    setTool('draw');  // natural next step
    trackEvent(hadPrevScale ? 'scope_recalibrated' : 'scope_scale_set', {
      path: 'draw', draft_id: draftId,
      properties: {
        floor_id: activeFloorId,
        metres,
        factor_m_per_px: factor,
        rooms_recomputed: hadPrevScale ? prevRoomCount : 0,
      },
    });
  };

  // ── Polygon drawing ───────────────────────────────────────────────────
  const handleAddPoint = useCallback((pt) => {
    updateFloor(activeFloorId, (f) => ({
      ...f,
      current_polygon_px: [...(f.current_polygon_px || []), pt],
    }));
  }, [activeFloorId, updateFloor]);

  const handleCloseRoom = useCallback(() => {
    const cur = floor?.current_polygon_px || [];
    if (cur.length < 3) return;
    setRoomDialog({ polygon_px: cur });
  }, [floor]);

  const commitRoom = ({ name, finish, doors, windows }) => {
    if (!roomDialog) return;
    const nm = (name || 'Room').toLowerCase();
    const isKitchen = nm.includes('kitchen');
    const isBathroom = nm.includes('bath') || nm.includes('wc') || nm.includes('shower') || nm.includes('en-suite') || nm.includes('ensuite');
    const newRoom = recalcRoomMeasures({
      id: `r_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name: name || 'Room',
      polygon_px: roomDialog.polygon_px,
      finish: finish || 'mid',       // legacy spec level, derived later
      // New explicit per-surface finishes — undefined forces the user to choose
      wall_finish: undefined,
      floor_finish: undefined,
      ceiling_finish: undefined,
      // Legacy single door count retained for backwards-compat reads, but the
      // per-type counters below are the source of truth sent to the engine.
      doors: Number(doors) || 0,
      doors_detail: { timber_single: Number(doors) || 0 },  // assume timber-single for initial dialog count
      windows: Number(windows) || 0,
      // Explicit per-room answers — left undefined so the RoomsPanel shows
      // the "please choose" nudge and the user must commit.
      strip_out: undefined,
      kitchen_supply: isKitchen ? undefined : 'na',
      bathroom_supply: isBathroom ? undefined : 'na',
      kitchen_spec: isKitchen ? 'mid' : undefined,
      bathroom_spec: isBathroom ? 'mid' : undefined,
    }, floor?.scale);
    updateFloor(activeFloorId, (f) => ({
      ...f,
      rooms: [...(f.rooms || []), newRoom],
      current_polygon_px: [],
    }));
    setRoomDialog(null);
    trackEvent('scope_room_added', {
      path: 'draw', draft_id: draftId,
      properties: {
        floor_id: activeFloorId,
        room_name: newRoom.name,
        area_m2: newRoom.area_m2,
        perimeter_m: newRoom.perimeter_m,
        finish: newRoom.finish,
        doors: newRoom.doors,
        windows: newRoom.windows,
        points: (roomDialog.polygon_px || []).length,
      },
    });
  };

  const cancelRoom = () => {
    // Keep the polygon — user hit Cancel by mistake should be able to resume
    setRoomDialog(null);
  };

  const undoPoint = () => {
    updateFloor(activeFloorId, (f) => ({
      ...f,
      current_polygon_px: (f.current_polygon_px || []).slice(0, -1),
    }));
  };

  const clearCurrent = () => {
    updateFloor(activeFloorId, (f) => ({ ...f, current_polygon_px: [] }));
  };

  const deleteRoom = (roomId) => {
    updateFloor(activeFloorId, (f) => ({
      ...f,
      rooms: (f.rooms || []).filter((r) => r.id !== roomId),
    }));
  };

  const renameRoom = (roomId, newName) => {
    updateFloor(activeFloorId, (f) => ({
      ...f,
      rooms: (f.rooms || []).map((r) => (r.id === roomId ? { ...r, name: newName } : r)),
    }));
  };

  const updateRoomField = (roomId, field, value) => {
    updateFloor(activeFloorId, (f) => ({
      ...f,
      rooms: (f.rooms || []).map((r) => {
        if (r.id !== roomId) return r;
        const next = { ...r, [field]: value };
        // Re-derive the internal `finish` spec level whenever an explicit surface
        // finish changes, so the engine path continues to work.
        if (field === 'wall_finish' || field === 'floor_finish' || field === 'ceiling_finish') {
          next.finish = deriveFinishLevel(next.wall_finish, next.floor_finish, next.ceiling_finish);
        }
        return next;
      }),
    }));
  };

  // Total rooms across all floors — drives the purchase-step gating
  const totalRoomsCount = Object.values(floorsMap).reduce(
    (n, f) => n + ((f && f.rooms) ? f.rooms.length : 0), 0
  );

  // Purchase-step helpers ───────────────────────────────────────────────
  // Route: draw → ai-review → purchase. The AI review step is skippable
  // but prominent, so we enter it from the Continue CTA and let ScopeAIReview
  // advance to 'purchase' on its own.
  const goToAIReview = async () => {
    if (totalRoomsCount === 0 || !draftId) return;
    try {
      await axios.post(`${API}/scope/drafts`, {
        draft_id: draftId,
        method: 'draw',
        address: addressForm.address,
        postcode: addressForm.postcode,
      });
    } catch (err) {
      console.warn('Pre-review draft save failed:', err?.message);
    }
    setPhase('ai-review');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPurchase = async () => {
    if (totalRoomsCount === 0 || !draftId) return;
    // Persist the address+postcode (not done live anywhere else on this page)
    try {
      await axios.post(`${API}/scope/drafts`, {
        draft_id: draftId,
        method: 'draw',
        address: addressForm.address,
        postcode: addressForm.postcode,
      });
    } catch (err) {
      // Best-effort draft save pre-checkout — surface in console so abandoned-
      // checkout debugging isn't blind if this ever silently fails.
      console.warn('Pre-checkout draft save failed:', err?.message);
    }
    setPhase('purchase');
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const res = await axios.post(`${API}/scope/drafts/${draftId}/calculate`);
      setPreview(res.data);
    } catch (err) {
      setPreviewError(err.response?.data?.error || 'Could not price your project. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const recalcPreview = async () => {
    setPreviewLoading(true);
    setPreviewError('');
    try {
      // Persist latest address+postcode first so the engine uses correct factor
      await axios.post(`${API}/scope/drafts`, {
        draft_id: draftId,
        method: 'draw',
        address: addressForm.address,
        postcode: addressForm.postcode,
      });
      const res = await axios.post(`${API}/scope/drafts/${draftId}/calculate`);
      setPreview(res.data);
    } catch (err) {
      setPreviewError(err.response?.data?.error || 'Could not reprice');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleTierCheckout = async (tier) => {
    if (!draftId) return;
    setCheckoutTier(tier);
    setCheckoutLoading(true);
    try {
      // Persist latest address so backend can compute region factor accurately
      await axios.post(`${API}/scope/drafts`, {
        draft_id: draftId,
        method: 'draw',
        address: addressForm.address,
        postcode: addressForm.postcode,
      });
      const res = await axios.post(`${API}/scope/tier-checkout`, {
        tier,
        draft_id: draftId,
        email: user?.email,
        address: addressForm.address,
        postcode: addressForm.postcode,
        scope_level: 'detailed',
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      setPreviewError(err.response?.data?.error || 'Checkout failed. Please try again.');
    } finally {
      setCheckoutLoading(false);
      setCheckoutTier(null);
    }
  };

  const handleRedeem = async () => {
    if (!draftId) return;
    setCheckoutLoading(true);
    setCheckoutTier('redeem');
    try {
      // Persist latest address first so the redeemed Full Project Pack uses accurate pricing
      await axios.post(`${API}/scope/drafts`, {
        draft_id: draftId,
        method: 'draw',
        address: addressForm.address,
        postcode: addressForm.postcode,
      });
      const res = await axios.post(`${API}/scope/redeem-subscription`, {
        draft_id: draftId,
        address: addressForm.address,
        postcode: addressForm.postcode,
      });
      if (res.data?.success) {
        // Success — route to success page (same as paid flow) to show the delivered pack
        window.location.href = `/scope/success?order=${encodeURIComponent(res.data.order_ref)}`;
      }
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === 'ALLOWANCE_EXCEEDED') {
        setPreviewError("You've used all your document downloads this month. Your allowance resets next billing cycle — or buy a one-off Full Project Pack below.");
      } else {
        setPreviewError(err.response?.data?.error || err.response?.data?.message || 'Redemption failed. Please try again.');
      }
    } finally {
      setCheckoutLoading(false);
      setCheckoutTier(null);
    }
  };

  // ── Loading gate ──────────────────────────────────────────────────────
  if (user === null || ensuring) {
    return (
      <div>
        <SiteNav cta="home" />
        <div style={{ padding: 80, textAlign: 'center', color: '#64748B' }}>Loading…</div>
      </div>
    );
  }

  const uploadedFloorIds = Object.keys(floorsMap).filter((fid) => floorsMap[fid]?.plan_image_url);
  const unuploadedFloors = FLOOR_OPTIONS.filter((f) => !uploadedFloorIds.includes(f.id));

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC' }} data-testid="scope-draw-page">
      <SiteNav cta="home" />
      {/* SCOPEai guided intro — shown once before the first-ever canvas visit */}
      {showIntro && (
        <ScopeAIIntro
          onStart={({ path }) => {
            // Track which path the user chose for funnel analysis
            try { trackEvent('scope_path_viewed', { path: 'draw', properties: { path_choice: path } }); } catch {}
            markIntroSeen();
          }}
          onSkip={markIntroSeen}
        />
      )}
      <div style={{ maxWidth: 1440, margin: '0 auto', padding: '24px 20px' }}>
        <button
          onClick={() => navigate('/scope')}
          style={{ background: 'none', border: 'none', color: '#64748B', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 18 }}
          data-testid="back-to-scope-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to SCOPE
        </button>

        {/* §3.9 — Project Info pre-filled from Indicative. Single Confirm-and-continue button. */}
        {!projectInfoConfirmed && !showIntro && (projectInfo.address || projectInfo.property_type) && (
          <div
            data-testid="project-info-confirm"
            style={{
              background: '#fff',
              border: '1.5px solid #E2E8F0',
              borderRadius: 12,
              padding: '22px 24px',
              marginBottom: 18,
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 10 }}>
              Project Info
            </div>
            <div style={{ fontSize: 15, color: '#0F172A', marginBottom: 4, fontWeight: 600 }}>
              {projectInfo.address || '—'}
            </div>
            <div style={{ fontSize: 13, color: '#475569', marginBottom: 14 }}>
              {[
                projectInfo.postcode,
                projectInfo.property_type && projectInfo.property_type.charAt(0).toUpperCase() + projectInfo.property_type.slice(1),
                projectInfo.scale_of_works && ({
                  cosmetic: 'Cosmetic refurb',
                  kitchen_bathroom: 'Kitchen and bathroom',
                  full: 'Full renovation',
                  extension: 'Extension',
                }[projectInfo.scale_of_works] || projectInfo.scale_of_works),
              ].filter(Boolean).join(' · ')}
            </div>
            <button
              onClick={() => setProjectInfoConfirmed(true)}
              data-testid="project-info-confirm-btn"
              style={{
                background: '#0F2742', color: '#fff', border: 'none', borderRadius: 9,
                padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Confirm and continue →
            </button>
          </div>
        )}

        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', padding: '12px 16px', borderRadius: 10, fontSize: 13, marginBottom: 16 }} data-testid="draw-error">
            {error}
          </div>
        )}

        {/* ── PURCHASE PHASE ───────────────────────────────────────── */}
        {phase === 'purchase' ? (
          <PurchaseScreen
            preview={preview}
            previewLoading={previewLoading}
            previewError={previewError}
            addressForm={addressForm}
            setAddressForm={setAddressForm}
            onBackToDraw={() => setPhase('draw')}
            onRecalc={recalcPreview}
            onCheckout={handleTierCheckout}
            onRedeem={handleRedeem}
            checkoutLoading={checkoutLoading}
            checkoutTier={checkoutTier}
            userEmail={user?.email}
            billing={billing}
          />
        ) : phase === 'ai-review' ? (
          <ScopeAIReview
            draftId={draftId}
            onBack={() => setPhase('draw')}
            onContinue={() => {
              setPhase('draw');  // bounce so goToPurchase resets cleanly
              goToPurchase();
            }}
          />
        ) : (
          <>
        {/* No floor uploaded yet → big upload card */}
        {uploadedFloorIds.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 14, padding: '36px 32px', border: '1.5px solid #E2E8F0', textAlign: 'center' }}>
            <div style={{ width: 54, height: 54, borderRadius: 12, background: '#EFF6FF', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <Upload className="w-6 h-6" style={{ color: '#2563EB' }} />
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#0F2742', marginBottom: 8 }}>Upload your floor plan</div>
            <p style={{ fontSize: 14, color: '#64748B', lineHeight: 1.6, maxWidth: 460, margin: '0 auto 24px' }}>
              JPG, PNG or PDF. We&apos;ll render it as your drawing surface — then you&apos;ll set the scale and measure your rooms directly on the plan.
            </p>
            <label
              ref={uploadTargetRef}
              style={{
                display: 'inline-block', background: '#2563EB', color: '#fff',
                padding: '12px 26px', borderRadius: 10, fontSize: 14, fontWeight: 700,
                cursor: uploading ? 'default' : 'pointer', opacity: uploading ? 0.55 : 1,
              }}
              data-testid="upload-plan-label"
            >
              {uploading ? 'Uploading…' : 'Choose a file'}
              <input
                type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => handleUpload(e, 'gf')}
                disabled={uploading}
                data-testid="upload-plan-input"
              />
            </label>
            <p style={{ fontSize: 11, color: '#94A3B8', marginTop: 14 }}>25 MB max · PDFs rendered from page 1</p>
          </div>
        ) : (
          <>
            {/* Floor tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }} data-testid="floor-tabs">
              {uploadedFloorIds.map((fid) => {
                const f = floorsMap[fid];
                return (
                  <button
                    key={fid}
                    onClick={() => setActiveFloorId(fid)}
                    data-testid={`floor-tab-${fid}`}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 8,
                      border: activeFloorId === fid ? '1.5px solid #2563EB' : '1.5px solid #E2E8F0',
                      background: activeFloorId === fid ? '#EFF6FF' : '#fff',
                      color: activeFloorId === fid ? '#1D4ED8' : '#334155',
                      fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    {f.label}
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#64748B', fontWeight: 600 }}>
                      {(f.rooms || []).length} {((f.rooms || []).length === 1) ? 'room' : 'rooms'}
                    </span>
                  </button>
                );
              })}
              {unuploadedFloors.length > 0 && (
                <button
                  onClick={() => setAddFloorOpen(true)}
                  data-testid="add-floor-btn"
                  style={{
                    padding: '8px 12px', borderRadius: 8, border: '1.5px dashed #94A3B8',
                    background: 'transparent', color: '#475569',
                    fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <Plus className="w-3 h-3" /> Add floor
                </button>
              )}
              <div style={{ marginLeft: 'auto', fontSize: 11, color: '#64748B', fontWeight: 600 }} data-testid="save-status">
                {saveStatus === 'saving' && 'Saving…'}
                {saveStatus === 'saved' && (<span style={{ color: '#16A34A' }}><Check className="w-3 h-3 inline" /> Saved</span>)}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 18, alignItems: 'start' }}>
              {/* LEFT — toolbar + canvas */}
              <div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }} data-testid="tool-toolbar">
                  <ToolButton active={tool === 'pan'} onClick={() => setTool('pan')} icon={<Move className="w-4 h-4" />} label="Pan" testid="tool-pan" />
                  <ToolButton
                    active={tool === 'calibrate'}
                    onClick={() => setTool('calibrate')}
                    icon={<Ruler className="w-4 h-4" />}
                    label={floor?.scale ? `Recalibrate (1 m ≈ ${(1 / floor.scale.factor_m_per_px).toFixed(1)} px)` : 'Set scale'}
                    highlight={!floor?.scale}
                    testid="tool-calibrate"
                    nodeRef={setCalibTargetEl}
                  />
                  <ToolButton
                    active={tool === 'draw'}
                    onClick={() => setTool('draw')}
                    icon={<Pencil className="w-4 h-4" />}
                    label="Draw room"
                    disabled={!floor?.scale}
                    testid="tool-draw"
                    nodeRef={setDrawTargetEl}
                  />
                  <div style={{ width: 1, height: 28, background: '#E2E8F0', margin: '0 4px' }} />
                  <button
                    onClick={undoPoint}
                    disabled={!floor?.current_polygon_px?.length}
                    data-testid="undo-point-btn"
                    style={miniBtnStyle(!floor?.current_polygon_px?.length)}
                  >
                    <Undo2 className="w-3 h-3" /> Undo point
                  </button>
                  <button
                    onClick={clearCurrent}
                    disabled={!floor?.current_polygon_px?.length}
                    data-testid="clear-current-btn"
                    style={miniBtnStyle(!floor?.current_polygon_px?.length)}
                  >
                    <X className="w-3 h-3" /> Clear
                  </button>
                  <div style={{ marginLeft: 'auto', fontSize: 11, color: '#64748B' }} data-testid="canvas-toolbar-hint">
                    {tool === 'pan' && (window.matchMedia('(pointer: coarse)').matches ? 'Drag · pinch to zoom' : 'Drag to pan · scroll to zoom · middle-click drag to pan')}
                    {tool === 'calibrate' && (window.matchMedia('(pointer: coarse)').matches ? 'Tap 2 points or press and hold for the magnifier' : 'Click two points on a known distance · middle-click drag to pan')}
                    {tool === 'draw' && !floor?.scale && 'Set scale first'}
                    {tool === 'draw' && floor?.scale && (window.matchMedia('(pointer: coarse)').matches ? 'Tap to place · press and hold for magnifier · Esc cancels' : 'Click to add points · click start to close · middle-click drag to pan · Esc cancels')}
                  </div>
                </div>

                {floor?.plan_image_url ? (
                  <DrawingCanvas
                    imageUrl={floor.plan_image_url}
                    imageWidth={floor.image_width}
                    imageHeight={floor.image_height}
                    rooms={floor.rooms || []}
                    currentPolygon={floor.current_polygon_px || []}
                    /* §3.5 — hide calibration line after scale saved. Only pass the line while actively calibrating. */
                    calibrationLine={tool === 'calibrate' && floor.scale ? [floor.scale.p1, floor.scale.p2] : null}
                    scale={floor.scale}
                    mode={tool}
                    onAddPoint={handleAddPoint}
                    onCalibratePoints={handleCalibratePoints}
                    onCloseRoom={handleCloseRoom}
                    containerWidth={1020}
                    containerHeight={700}
                  />
                ) : (
                  <div style={{ background: '#fff', border: '1.5px dashed #E2E8F0', borderRadius: 12, padding: 40, textAlign: 'center', color: '#64748B', fontSize: 13 }}>
                    No plan uploaded for {floor?.label || 'this floor'}.
                    <div style={{ marginTop: 12 }}>
                      <label style={{ display: 'inline-block', background: '#2563EB', color: '#fff', padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        Upload plan
                        <input
                          type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                          style={{ display: 'none' }}
                          onChange={(e) => handleUpload(e, activeFloorId)}
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT — rooms panel */}
              <RoomsPanel
                floor={floor}
                onDeleteRoom={deleteRoom}
                onRenameRoom={renameRoom}
                onRoomField={updateRoomField}
              />
            </div>

            {/* Continue to purchase */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 12, color: '#64748B' }}>
                {totalRoomsCount === 0
                  ? 'Draw at least one room to price your documents.'
                  : (() => {
                      // Count rooms still missing answers
                      const allRooms = Object.values(floorsMap).flatMap(f => f.rooms || []);
                      const isIncomplete = (r) => {
                        const nm = (r.name || '').toLowerCase();
                        const isKitchen = nm.includes('kitchen');
                        const isBathroom = nm.includes('bath') || nm.includes('wc') || nm.includes('shower') || nm.includes('en-suite') || nm.includes('ensuite');
                        if (!r.wall_finish || !r.floor_finish || !r.ceiling_finish) return true;
                        if (r.strip_out === undefined) return true;
                        if (isKitchen && !r.kitchen_supply) return true;
                        if (isBathroom && !r.bathroom_supply) return true;
                        return false;
                      };
                      const missing = allRooms.filter(isIncomplete);
                      if (missing.length > 0) {
                        return `${missing.length} room${missing.length === 1 ? '' : 's'} still need answers (finishes, strip-out or supply method).`;
                      }
                      return `${totalRoomsCount} ${totalRoomsCount === 1 ? 'room' : 'rooms'} drawn across ${Object.keys(floorsMap).filter(f => (floorsMap[f].rooms || []).length > 0).length || 1} floor${Object.keys(floorsMap).filter(f => (floorsMap[f].rooms || []).length > 0).length > 1 ? 's' : ''}.`;
                    })()
                }
              </div>
              {(() => {
                const allRooms = Object.values(floorsMap).flatMap(f => f.rooms || []);
                const hasUnanswered = allRooms.some((r) => {
                  const nm = (r.name || '').toLowerCase();
                  const isKitchen = nm.includes('kitchen');
                  const isBathroom = nm.includes('bath') || nm.includes('wc') || nm.includes('shower') || nm.includes('en-suite') || nm.includes('ensuite');
                  if (!r.wall_finish || !r.floor_finish || !r.ceiling_finish) return true;
                  if (r.strip_out === undefined) return true;
                  if (isKitchen && !r.kitchen_supply) return true;
                  if (isBathroom && !r.bathroom_supply) return true;
                  return false;
                });
                const disabled = totalRoomsCount === 0 || hasUnanswered;
                return (
                  <button
                    onClick={goToAIReview}
                    disabled={disabled}
                    data-testid="continue-to-purchase-btn"
                    style={{
                      background: disabled ? '#94A3B8' : '#0F2742', color: '#fff', border: 'none',
                      padding: '12px 22px', borderRadius: 10, fontSize: 14, fontWeight: 700,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Review with SCOPEai →
                  </button>
                );
              })()}
            </div>
          </>
        )}
          </>
        )}
      </div>

      {/* Calibration dialog */}
      {calibDialog && (
        <Dialog
          title="Set scale"
          testid="calibration-dialog"
          onClose={() => { setCalibDialog(null); }}
        >
          <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.55, margin: '0 0 14px' }}>
            Distance between the two points: <strong>{calibDialog.pxLen.toFixed(0)} px</strong>.
            <br />How long is this line in real life?
          </p>
          <CalibrationForm onCommit={commitCalibration} onCancel={() => setCalibDialog(null)} />
        </Dialog>
      )}

      {/* Room dialog */}
      {roomDialog && (
        <Dialog
          title="Save room"
          testid="room-dialog"
          onClose={cancelRoom}
        >
          <RoomForm
            areaM2={floor?.scale?.factor_m_per_px ? pxAreaToM2(polygonAreaPx(roomDialog.polygon_px), floor.scale.factor_m_per_px) : null}
            onCommit={commitRoom}
            onCancel={cancelRoom}
          />
        </Dialog>
      )}

      {/* Add floor dialog */}
      {addFloorOpen && (
        <Dialog
          title="Add a floor"
          testid="add-floor-dialog"
          onClose={() => setAddFloorOpen(false)}
        >
          <p style={{ fontSize: 13, color: '#475569', margin: '0 0 14px' }}>Choose which floor to add:</p>
          <div style={{ display: 'grid', gap: 8 }}>
            {unuploadedFloors.map((f) => (
              <label
                key={f.id}
                style={{ display: 'block', padding: '10px 14px', background: '#F8FAFC', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 13, fontWeight: 600, color: '#0F2742', cursor: 'pointer' }}
                data-testid={`add-floor-${f.id}`}
              >
                {f.label}
                <input
                  type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => handleUpload(e, f.id)}
                />
              </label>
            ))}
          </div>
        </Dialog>
      )}

      {/* First-use walkthrough — §7.4: only show AFTER SCOPEai intro modal dismissed */}
      {phase === 'draw' && !showIntro && !userFlags.scope_draw_walkthrough_seen && walkStep < 99 && (
        <>
          {walkStep === 1 && (
            <WalkthroughTip
              target={uploadTargetRef.current}
              placement="bottom"
              step={1}
              totalSteps={3}
              title="Start by uploading your floor plan"
              body="JPG, PNG or PDF accepted. We'll render it as your drawing surface."
              onNext={() => setWalkStep(2)}
              onDismiss={dismissWalk}
            />
          )}
          {walkStep === 2 && (
            <WalkthroughTip
              target={calibTargetEl}
              placement="bottom"
              step={2}
              totalSteps={3}
              title="Set the scale first"
              body="Click two points on a wall or dimension you know the real length of. This tells SCOPE how big your rooms are."
              onNext={() => setWalkStep(3)}
              onDismiss={dismissWalk}
            />
          )}
          {walkStep === 3 && (
            <WalkthroughTip
              target={drawTargetEl}
              placement="bottom"
              step={3}
              totalSteps={3}
              title="Now draw your first room"
              body="Click each corner, then click back to the start point to close it."
              isLast
              onNext={completeWalk}
              onDismiss={dismissWalk}
            />
          )}
        </>
      )}
    </div>
  );
};

// ── Inline helper components ──────────────────────────────────────────

const ToolButton = ({ active, onClick, icon, label, disabled, highlight, testid, nodeRef }) => (
  <button
    ref={nodeRef}
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '8px 12px',
      border: active ? '1.5px solid #2563EB' : (highlight ? '1.5px solid #F59E0B' : '1.5px solid #E2E8F0'),
      background: active ? '#EFF6FF' : (highlight ? '#FFFBEB' : '#fff'),
      color: disabled ? '#94A3B8' : (active ? '#1D4ED8' : (highlight ? '#92400E' : '#334155')),
      fontSize: 12, fontWeight: 700,
      borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    }}
  >
    {icon} {label}
  </button>
);

const miniBtnStyle = (disabled) => ({
  display: 'flex', alignItems: 'center', gap: 4,
  padding: '7px 10px',
  border: '1px solid #E2E8F0',
  background: '#fff',
  color: disabled ? '#94A3B8' : '#475569',
  fontSize: 11, fontWeight: 700,
  borderRadius: 7, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
});

const Dialog = ({ title, testid, onClose, children }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,39,66,.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
      data-testid={testid}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, padding: 24, width: 420, maxWidth: '92vw',
          boxShadow: '0 20px 60px rgba(15,39,66,.2)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#0F2742' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }} data-testid={`${testid}-close`}>
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const CalibrationForm = ({ onCommit, onCancel }) => {
  const [val, setVal] = useState('');
  const metres = parseFloat(val);
  const valid = Number.isFinite(metres) && metres > 0;
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (valid) onCommit(metres); }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: '#334155', letterSpacing: '.05em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
        Real-world length (metres)
      </label>
      <input
        type="number" step="0.01" min="0.01"
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="e.g. 4.20"
        data-testid="calibration-input"
        style={{
          width: '100%', padding: '10px 12px', border: '1.5px solid #E2E8F0',
          borderRadius: 8, fontSize: 14, color: '#0F2742',
        }}
      />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button
          type="button" onClick={onCancel}
          style={{ background: '#fff', border: '1.5px solid #E2E8F0', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#475569', cursor: 'pointer' }}
          data-testid="calibration-cancel-btn"
        >Cancel</button>
        <button
          type="submit" disabled={!valid}
          style={{ background: valid ? '#2563EB' : '#94A3B8', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: valid ? 'pointer' : 'not-allowed' }}
          data-testid="calibration-save-btn"
        >Save scale</button>
      </div>
    </form>
  );
};

const RoomForm = ({ areaM2, onCommit, onCancel }) => {
  const [name, setName] = useState('');
  const [finish, setFinish] = useState('mid');
  const [doors, setDoors] = useState(1);
  const [windows, setWindows] = useState(1);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onCommit({ name: name.trim() || 'Room', finish, doors, windows }); }}>
      {areaM2 != null && (
        <div style={{ background: '#F0FDF4', border: '1px solid #86EFAC', color: '#166534', padding: '10px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
          Measured area: {areaM2} m²
        </div>
      )}
      <label style={{ fontSize: 11, fontWeight: 700, color: '#334155', letterSpacing: '.05em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Room name</label>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Kitchen, Living Room, Bedroom 1"
        data-testid="room-name-input"
        style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 14, color: '#0F2742', marginBottom: 14 }}
      />

      {/* Finish level toggle removed per §3.3 spec — room card is the source of truth for finishes.
          `finish` state is kept only so older draft rooms don't crash on reload. */}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <Counter label="Doors" value={doors} onChange={setDoors} testid="room-doors" />
        <Counter label="Windows" value={windows} onChange={setWindows} testid="room-windows" />
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button" onClick={onCancel}
          style={{ background: '#fff', border: '1.5px solid #E2E8F0', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#475569', cursor: 'pointer' }}
          data-testid="room-cancel-btn"
        >Cancel</button>
        <button
          type="submit"
          style={{ background: '#2563EB', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          data-testid="room-save-btn"
        >Save room</button>
      </div>
    </form>
  );
};

const Counter = ({ label, value, onChange, testid }) => (
  <div>
    <label style={{ fontSize: 11, fontWeight: 700, color: '#334155', letterSpacing: '.05em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>{label}</label>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <button
        type="button" onClick={() => onChange(Math.max(0, value - 1))}
        style={{ width: 32, height: 32, border: '1.5px solid #E2E8F0', background: '#fff', borderRadius: 7, fontSize: 16, fontWeight: 800, cursor: 'pointer', color: '#334155' }}
        data-testid={`${testid}-dec`}
      >−</button>
      <div style={{ flex: 1, textAlign: 'center', padding: '7px 10px', border: '1.5px solid #E2E8F0', borderRadius: 7, fontSize: 14, fontWeight: 700, color: '#0F2742' }} data-testid={`${testid}-value`}>{value}</div>
      <button
        type="button" onClick={() => onChange(Math.min(20, value + 1))}
        style={{ width: 32, height: 32, border: '1.5px solid #E2E8F0', background: '#fff', borderRadius: 7, fontSize: 16, fontWeight: 800, cursor: 'pointer', color: '#334155' }}
        data-testid={`${testid}-inc`}
      >+</button>
    </div>
  </div>
);

const RoomsPanel = ({ floor, onDeleteRoom, onRenameRoom, onRoomField }) => {
  const rooms = floor?.rooms || [];
  const totalArea = rooms.reduce((s, r) => s + (r.area_m2 || 0), 0);
  return (
    <div style={{ background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 12, padding: 16, minHeight: 300 }} data-testid="rooms-panel">
      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>
        {floor?.label || 'Floor'}
      </div>
      <div style={{ fontSize: 17, fontWeight: 800, color: '#0F2742', marginBottom: 14 }}>
        Rooms ({rooms.length})
      </div>

      {!floor?.scale && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E', fontSize: 12, padding: '10px 12px', borderRadius: 8, marginBottom: 14, lineHeight: 1.5 }}>
          Set the scale first (pick a known distance on the plan) so we can calculate areas.
        </div>
      )}

      {rooms.length === 0 && floor?.scale && (
        <div style={{ fontSize: 13, color: '#64748B', lineHeight: 1.55, padding: '16px 0' }}>
          Use the <strong>Draw room</strong> tool to trace the walls of each room, then close the polygon and give it a name.
        </div>
      )}

      <div style={{ display: 'grid', gap: 8 }}>
        {rooms.map((r) => (
          <div key={r.id} data-testid={`room-card-${r.id}`} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                value={r.name}
                onChange={(e) => onRenameRoom(r.id, e.target.value)}
                data-testid={`room-name-${r.id}`}
                style={{ flex: 1, background: 'transparent', border: 'none', fontSize: 14, fontWeight: 700, color: '#0F2742', outline: 'none' }}
              />
              <button
                onClick={() => onDeleteRoom(r.id)}
                data-testid={`room-delete-${r.id}`}
                style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: 2 }}
                title="Delete room"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600, marginBottom: 8 }}>
              {r.area_m2 != null ? `${r.area_m2} m²` : '–'} · {r.perimeter_m != null ? `${r.perimeter_m} m perimeter` : '–'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 8 }}>
              {/* Wall finish */}
              <div data-testid={`room-${r.id}-wall-finish-panel`}>
                <label style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em' }}>Walls</label>
                <select
                  value={r.wall_finish || ''}
                  onChange={(e) => onRoomField(r.id, 'wall_finish', e.target.value || undefined)}
                  data-testid={`room-${r.id}-wall-finish`}
                  style={{ width: '100%', padding: '6px 6px', fontSize: 11, border: '1px solid #CBD5E1', borderRadius: 5, background: '#fff', marginTop: 2 }}
                >
                  <option value="">Choose…</option>
                  {WALL_FINISH_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
              {/* Floor finish */}
              <div data-testid={`room-${r.id}-floor-finish-panel`}>
                <label style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em' }}>Floor</label>
                <select
                  value={r.floor_finish || ''}
                  onChange={(e) => onRoomField(r.id, 'floor_finish', e.target.value || undefined)}
                  data-testid={`room-${r.id}-floor-finish`}
                  style={{ width: '100%', padding: '6px 6px', fontSize: 11, border: '1px solid #CBD5E1', borderRadius: 5, background: '#fff', marginTop: 2 }}
                >
                  <option value="">Choose…</option>
                  {FLOOR_FINISH_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
              {/* Ceiling finish */}
              <div data-testid={`room-${r.id}-ceiling-finish-panel`}>
                <label style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em' }}>Ceiling</label>
                <select
                  value={r.ceiling_finish || ''}
                  onChange={(e) => onRoomField(r.id, 'ceiling_finish', e.target.value || undefined)}
                  data-testid={`room-${r.id}-ceiling-finish`}
                  style={{ width: '100%', padding: '6px 6px', fontSize: 11, border: '1px solid #CBD5E1', borderRadius: 5, background: '#fff', marginTop: 2 }}
                >
                  <option value="">Choose…</option>
                  {CEILING_FINISH_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
            </div>
            {(!r.wall_finish || !r.floor_finish || !r.ceiling_finish) && (
              <div style={{ fontSize: 10, color: '#B45309', marginTop: -4, marginBottom: 6 }}>
                Choose wall, floor and ceiling finishes — they drive the SOW.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 11, color: '#64748B', fontWeight: 600 }}>
              {/* ── Doors — 6 typed counters ── */}
              <div data-testid={`room-${r.id}-doors-panel`}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#94A3B8', fontWeight: 700, marginBottom: 4 }}>
                  Doors (count per type)
                </div>
                {[
                  { key: 'timber_single',   label: 'Timber single (820mm)' },
                  { key: 'timber_double',   label: 'Timber double (1600mm)' },
                  { key: 'sliding_double',  label: 'Sliding double (1600mm)' },
                  { key: 'sliding_pocket',  label: 'Pocket sliding (1000mm)' },
                  { key: 'external_single', label: 'External single (820mm)' },
                  { key: 'external_double', label: 'External double (1800mm)' },
                ].map(({ key, label }) => {
                  const count = (r.doors_detail?.[key]) || 0;
                  const setCount = (delta) => {
                    const next = Math.max(0, Math.min(20, count + delta));
                    const detail = { ...(r.doors_detail || {}), [key]: next };
                    onRoomField(r.id, 'doors_detail', detail);
                  };
                  return (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, padding: '3px 0' }}>
                      <span style={{ flex: 1, color: '#475569', fontWeight: 500 }}>{label}</span>
                      <button
                        type="button"
                        onClick={() => setCount(-1)}
                        disabled={count === 0}
                        data-testid={`room-${r.id}-door-${key}-dec`}
                        style={{ width: 22, height: 22, border: '1px solid #CBD5E1', background: '#fff', borderRadius: 4, cursor: count === 0 ? 'not-allowed' : 'pointer', opacity: count === 0 ? 0.5 : 1, fontWeight: 700 }}
                      >−</button>
                      <span data-testid={`room-${r.id}-door-${key}-count`} style={{ minWidth: 18, textAlign: 'center', fontVariantNumeric: 'tabular-nums', color: '#0F2742', fontWeight: 700 }}>{count}</span>
                      <button
                        type="button"
                        onClick={() => setCount(+1)}
                        data-testid={`room-${r.id}-door-${key}-inc`}
                        style={{ width: 22, height: 22, border: '1px solid #CBD5E1', background: '#fff', borderRadius: 4, cursor: 'pointer', fontWeight: 700 }}
                      >+</button>
                    </div>
                  );
                })}
              </div>
              {/* ── Windows — single typed counter ── */}
              <div data-testid={`room-${r.id}-windows-panel`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                <span style={{ flex: 1, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#94A3B8', fontWeight: 700 }}>Windows</span>
                <button
                  type="button"
                  onClick={() => onRoomField(r.id, 'windows', Math.max(0, (r.windows ?? 0) - 1))}
                  disabled={(r.windows ?? 0) === 0}
                  data-testid={`room-${r.id}-window-dec`}
                  style={{ width: 22, height: 22, border: '1px solid #CBD5E1', background: '#fff', borderRadius: 4, cursor: (r.windows ?? 0) === 0 ? 'not-allowed' : 'pointer', opacity: (r.windows ?? 0) === 0 ? 0.5 : 1, fontWeight: 700 }}
                >−</button>
                <span data-testid={`room-${r.id}-window-count`} style={{ minWidth: 18, textAlign: 'center', fontVariantNumeric: 'tabular-nums', color: '#0F2742', fontWeight: 700 }}>{r.windows ?? 0}</span>
                <button
                  type="button"
                  onClick={() => onRoomField(r.id, 'windows', Math.min(20, (r.windows ?? 0) + 1))}
                  data-testid={`room-${r.id}-window-inc`}
                  style={{ width: 22, height: 22, border: '1px solid #CBD5E1', background: '#fff', borderRadius: 4, cursor: 'pointer', fontWeight: 700 }}
                >+</button>
              </div>
              {/* ── Strip-out Y/N — explicit, no default ── */}
              <div data-testid={`room-${r.id}-strip-panel`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, padding: '6px 0 0', borderTop: '1px dashed #E2E8F0' }}>
                <span style={{ flex: 1, color: '#475569' }}>Strip out before works?</span>
                {[{ id: true, label: 'Yes' }, { id: false, label: 'No' }].map((o) => {
                  const isActive = r.strip_out === o.id;
                  return (
                    <button
                      key={String(o.id)}
                      type="button"
                      onClick={() => onRoomField(r.id, 'strip_out', o.id)}
                      data-testid={`room-${r.id}-strip-${o.label.toLowerCase()}`}
                      style={{
                        padding: '4px 10px', fontSize: 11, fontWeight: 700,
                        border: '1px solid', borderColor: isActive ? '#0F2742' : '#CBD5E1',
                        background: isActive ? '#0F2742' : '#fff',
                        color: isActive ? '#fff' : '#475569',
                        borderRadius: 5, cursor: 'pointer',
                      }}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              {r.strip_out === undefined && (
                <div style={{ fontSize: 10, color: '#B45309', marginTop: -4 }}>
                  Please choose — strip-out affects the SOW price.
                </div>
              )}
              {/* ── Kitchen / Bathroom supply — PC sum terminology ── */}
              {(() => {
                const nm = (r.name || '').toLowerCase();
                const isKitchen = nm.includes('kitchen');
                const isBathroom = nm.includes('bath') || nm.includes('wc') || nm.includes('shower') || nm.includes('en-suite') || nm.includes('ensuite');
                if (!isKitchen && !isBathroom) return null;
                const field = isKitchen ? 'kitchen_supply' : 'bathroom_supply';
                const val = r[field];
                return (
                  <div data-testid={`room-${r.id}-${isKitchen ? 'kitchen' : 'bathroom'}-supply-panel`} style={{ paddingTop: 6, borderTop: '1px dashed #E2E8F0' }}>
                    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#94A3B8', fontWeight: 700, marginBottom: 4 }}>
                      {isKitchen ? 'Kitchen' : 'Bathroom'} — supply method
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {[
                        { id: 'contractor', label: 'Contractor supply and fit', desc: 'We source + install everything' },
                        { id: 'client',     label: 'Client supply (PC sum)',   desc: 'You procure the fittings; we allow a PC sum and install' },
                      ].map((o) => {
                        const isActive = val === o.id;
                        return (
                          <button
                            key={o.id}
                            type="button"
                            onClick={() => onRoomField(r.id, field, o.id)}
                            data-testid={`room-${r.id}-supply-${o.id}`}
                            style={{
                              padding: '6px 10px', textAlign: 'left',
                              border: '1px solid', borderColor: isActive ? '#1D4ED8' : '#CBD5E1',
                              background: isActive ? '#EFF6FF' : '#fff',
                              borderRadius: 6, cursor: 'pointer',
                            }}
                          >
                            <div style={{ fontSize: 11, fontWeight: 700, color: isActive ? '#1D4ED8' : '#0F2742' }}>{o.label}</div>
                            <div style={{ fontSize: 10, color: '#64748B', fontWeight: 500, marginTop: 1 }}>{o.desc}</div>
                          </button>
                        );
                      })}
                    </div>
                    {val === undefined && (
                      <div style={{ fontSize: 10, color: '#B45309', marginTop: 4 }}>
                        Please choose — affects the {isKitchen ? 'kitchen' : 'bathroom'} line items.
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            {/* Kitchen spec picker — only for rooms named "kitchen" with contractor supply */}
            {((r.name || '').toLowerCase().includes('kitchen') && r.kitchen_supply === 'contractor') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748B', fontWeight: 600, marginTop: 4 }} data-testid={`room-${r.id}-kitchen-spec`}>
                <span>Kitchen spec:</span>
                <select
                  value={r.kitchen_spec || 'mid'}
                  onChange={(e) => onRoomField(r.id, 'kitchen_spec', e.target.value)}
                  style={{ padding: '2px 4px', fontSize: 11, border: '1px solid #E2E8F0', borderRadius: 4, background: '#fff' }}
                >
                  <option value="budget">Budget (flat-pack)</option>
                  <option value="mid">Mid-range (shaker)</option>
                  <option value="high">High-end (premium + quartz)</option>
                </select>
              </div>
            )}
            {/* Bathroom spec picker — only for bathroom rooms with contractor supply */}
            {((() => {
              const nm = (r.name || '').toLowerCase();
              const isBath = nm.includes('bath') || nm.includes('wc') || nm.includes('shower') || nm.includes('en-suite') || nm.includes('ensuite');
              return isBath && r.bathroom_supply === 'contractor';
            })()) && (
              <div style={{ marginTop: 6 }} data-testid={`room-${r.id}-bathroom-spec`}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#94A3B8', fontWeight: 700, marginBottom: 4 }}>Bathroom spec</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {BATHROOM_SPEC_OPTIONS.map((o) => {
                    const isActive = (r.bathroom_spec || 'mid') === o.id;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => onRoomField(r.id, 'bathroom_spec', o.id)}
                        data-testid={`room-${r.id}-bathroom-spec-${o.id}`}
                        style={{
                          padding: '6px 10px', textAlign: 'left',
                          border: '1px solid', borderColor: isActive ? '#1D4ED8' : '#CBD5E1',
                          background: isActive ? '#EFF6FF' : '#fff',
                          borderRadius: 6, cursor: 'pointer',
                        }}
                      >
                        <div style={{ fontSize: 11, fontWeight: 700, color: isActive ? '#1D4ED8' : '#0F2742' }}>{o.label}</div>
                        <div style={{ fontSize: 10, color: '#64748B', fontWeight: 500, marginTop: 1 }}>{o.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Per-room spec override — optional, defaults to project spec level.
                Affects walls / floor / ceiling / door rate tiers for this room only. */}
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748B', fontWeight: 600 }} data-testid={`room-${r.id}-spec-override`}>
              <span title="Leave blank to use the project-wide spec level">Room spec override:</span>
              <select
                value={r.spec_override || ''}
                onChange={(e) => onRoomField(r.id, 'spec_override', e.target.value || null)}
                data-testid={`room-${r.id}-spec-override-select`}
                style={{ padding: '2px 4px', fontSize: 11, border: '1px solid #E2E8F0', borderRadius: 4, background: '#fff' }}
              >
                <option value="">Use project spec</option>
                <option value="low">Value</option>
                <option value="mid">Standard</option>
                <option value="high">Premium</option>
              </select>
            </div>
          </div>
        ))}
      </div>

      {rooms.length > 0 && (
        <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 12, marginTop: 14, fontSize: 13, fontWeight: 700, color: '#0F2742' }} data-testid="total-area">
          Total area: {totalArea.toFixed(2)} m²
        </div>
      )}
    </div>
  );
};

export default ScopeDrawPage;

// ── Purchase Screen ────────────────────────────────────────────────
const PurchaseScreen = ({
  preview, previewLoading, previewError,
  addressForm, setAddressForm,
  onBackToDraw, onRecalc, onCheckout, onRedeem,
  checkoutLoading, checkoutTier, userEmail, billing,
}) => {
  const tiers = [
    { id: 'scope_materials', label: 'Materials Schedule', price: 29,
      features: ['Per-trade materials list', 'Spec guide for 8 trades', 'Supply-only pricing'],
      testid: 'tier-materials' },
    { id: 'scope_sow', label: 'Schedule of Works', price: 119,
      features: ['Full measured SOW', 'Trade-by-trade breakdown', 'Tender-ready document'],
      testid: 'tier-sow' },
    { id: 'scope_full', label: 'Full Project Pack', price: 199,
      features: ['Full measured SOW', 'Detailed cost estimate', 'Key materials schedule', 'What-to-do-next guide'],
      featured: true,
      testid: 'tier-full' },
  ];
  // Subscriber redemption state
  const isSub = billing && (billing.tier === 'pro' || billing.tier === 'pro_plus');
  const allowance = billing?.monthly_allowances;
  const remaining = allowance?.downloads_remaining ?? 0;
  const cap = allowance?.downloads_cap ?? 0;
  const resetOn = allowance?.reset_on ? new Date(allowance.reset_on).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : null;
  return (
    <div data-testid="purchase-screen">
      <button
        onClick={onBackToDraw}
        data-testid="back-to-draw-btn"
        style={{ background: 'none', border: 'none', color: '#64748B', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 18 }}
      >
        <ArrowLeft className="w-4 h-4" /> Back to drawing
      </button>

      {/* §5.6 — address and postcode are already captured upstream. Purchase screen uses those values silently. */}

      {/* Live cost range */}
      <div style={{ background: '#0F2742', color: '#fff', borderRadius: 14, padding: '22px 26px', marginBottom: 24 }} data-testid="cost-range-card">
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', opacity: 0.7, marginBottom: 6 }}>
          Indicative budget range
        </div>
        {previewError ? (
          <div style={{ fontSize: 13, color: '#FCA5A5' }} data-testid="preview-error">{previewError}</div>
        ) : previewLoading && !preview ? (
          <div style={{ fontSize: 14, opacity: 0.85 }}>Calculating…</div>
        ) : preview ? (
          <>
            <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.02em' }} data-testid="cost-range-value">
              £{(preview.total_low || 0).toLocaleString()} – £{(preview.total_high || 0).toLocaleString()}
            </div>
            <div data-testid="draw-range-qualifier" style={{
              fontSize: 12, fontStyle: 'italic', opacity: 0.7, marginTop: 4,
            }}>
              dependent on specification and defined scope
            </div>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
              {preview.rooms_count} room{preview.rooms_count === 1 ? '' : 's'} · {preview.total_area_m2} m²
              {preview.region && ` · ${preview.region}`}
            </div>
            {typeof preview.location_factor === 'number' && (
              <div data-testid="location-factor-chip" style={{
                display: 'inline-block',
                marginTop: 10,
                padding: '5px 12px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.12)',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '.01em',
              }}>
                Costs adjusted for your region ({(() => {
                  const delta = (Number(preview.location_factor) - 1) * 100;
                  if (Math.abs(delta) < 0.05) return 'in line with UK average';
                  const sign = delta > 0 ? '+' : '−';
                  return `${sign}${Math.abs(delta).toFixed(1)}% vs UK average`;
                })()})
              </div>
            )}
            {preview.spec_level && (
              <div data-testid="spec-level-chip" style={{
                display: 'inline-block',
                marginTop: 10, marginLeft: 8,
                padding: '5px 12px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.12)',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '.01em',
              }}>
                {preview.spec_level === 'low' ? 'Value finish' : preview.spec_level === 'high' ? 'Premium finish' : 'Standard finish'}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 14, opacity: 0.85 }}>Confirm your address to see your budget range.</div>
        )}
      </div>

      {/* Subscriber Redeem CTA — Pro / Pro+ only */}
      {isSub && remaining > 0 && (
        <div
          data-testid="redeem-card"
          style={{
            background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
            border: '2px solid #2563EB',
            borderRadius: 14, padding: '18px 22px', marginBottom: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 16, flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 10, background: '#2563EB',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Check className="w-5 h-5" style={{ color: '#fff' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', color: '#1D4ED8', textTransform: 'uppercase' }}>
                {billing.tier === 'pro_plus' ? 'Pro+ subscriber' : 'Pro subscriber'}
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#0F2742', marginTop: 2 }}>
                Redeem your Full Project Pack this month
              </div>
              <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
                <strong data-testid="redeem-remaining">{remaining} of {cap} downloads</strong> remaining
                {resetOn && <span> · resets {resetOn}</span>}
              </div>
            </div>
          </div>
          <button
            onClick={onRedeem}
            disabled={checkoutLoading || !preview}
            data-testid="redeem-btn"
            style={{
              padding: '12px 22px',
              background: (checkoutLoading || !preview) ? '#94A3B8' : '#2563EB',
              color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 800,
              cursor: (checkoutLoading || !preview) ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {checkoutLoading && checkoutTier === 'redeem' ? 'Redeeming…' : 'Redeem now — £0'}
          </button>
        </div>
      )}

      {/* Subscriber Cap-Reached Notice */}
      {isSub && remaining === 0 && cap > 0 && (
        <div
          data-testid="redeem-exhausted"
          style={{
            background: '#FEF3C7', border: '1.5px solid #FDE68A',
            borderRadius: 12, padding: '14px 18px', marginBottom: 18,
            fontSize: 13, color: '#92400E',
          }}
        >
          <strong>You've used all {cap} document downloads this month.</strong>{' '}
          Your allowance resets {resetOn ? `on ${resetOn}` : 'next billing cycle'}. You can still purchase a one-off Full Project Pack below.
        </div>
      )}

      {/* 3-tier purchase cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }} data-testid="tier-cards">
        {tiers.map((t) => (
          <div
            key={t.id}
            data-testid={t.testid}
            style={{
              background: '#fff',
              border: t.featured ? '2px solid #2563EB' : '1.5px solid #E2E8F0',
              borderRadius: 12, padding: '22px 20px',
              position: 'relative',
              boxShadow: t.featured ? '0 8px 24px rgba(37,99,235,.12)' : 'none',
            }}
          >
            {t.featured && (
              <div style={{ position: 'absolute', top: -10, right: 16, background: '#2563EB', color: '#fff', padding: '2px 10px', fontSize: 10, fontWeight: 800, letterSpacing: '.05em', borderRadius: 20, textTransform: 'uppercase' }}>
                Best value
              </div>
            )}
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0F2742', marginBottom: 4 }}>{t.label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#0F2742', letterSpacing: '-0.02em', marginBottom: 12 }}>
              £{t.price}
              <span style={{ fontSize: 11, fontWeight: 600, color: '#64748B', marginLeft: 6 }}>One-off</span>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 14px', fontSize: 12, color: '#475569', lineHeight: 1.7 }}>
              {t.features.map((f, i) => (
                <li key={i} style={{ display: 'flex', gap: 6 }}>
                  <Check className="w-3 h-3" style={{ color: '#16A34A', marginTop: 4, flexShrink: 0 }} /> {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => onCheckout(t.id)}
              disabled={checkoutLoading || !preview}
              data-testid={`${t.testid}-buy-btn`}
              style={{
                width: '100%', padding: '11px 16px',
                background: (checkoutLoading || !preview) ? '#94A3B8' : (t.featured ? '#2563EB' : '#0F2742'),
                color: '#fff', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 700,
                cursor: (checkoutLoading || !preview) ? 'not-allowed' : 'pointer',
              }}
            >
              {checkoutLoading && checkoutTier === t.id ? 'Starting checkout…' : `Buy ${t.label}`}
            </button>
          </div>
        ))}
      </div>

      {/* §5.5 — QS Check upsell tile with MOST POPULAR tag */}
      <div
        data-testid="qs-check-upsell-tile"
        style={{
          marginTop: 18,
          background: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)',
          border: '2px solid #F59E0B',
          borderRadius: 12,
          padding: '18px 22px',
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, flexWrap: 'wrap',
        }}
      >
        <div style={{
          position: 'absolute', top: -10, left: 18,
          background: '#F59E0B', color: '#fff',
          padding: '3px 12px', fontSize: 10, fontWeight: 800,
          letterSpacing: '.06em', borderRadius: 20, textTransform: 'uppercase',
        }}>
          MOST POPULAR
        </div>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#0F2742', marginBottom: 4 }}>
            QS Check — verified estimate review
          </div>
          <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.55 }}>
            One of our Quantity Surveyors reviews your figures, flags assumptions and gives you confidence before committing to a contractor. Delivered in 2 working days.
          </div>
        </div>
        <button
          onClick={() => onCheckout('qs_check')}
          disabled={checkoutLoading || !preview}
          data-testid="qs-check-buy-btn"
          style={{
            padding: '12px 22px',
            background: (checkoutLoading || !preview) ? '#94A3B8' : '#0F2742',
            color: '#fff', border: 'none', borderRadius: 10,
            fontSize: 14, fontWeight: 800,
            cursor: (checkoutLoading || !preview) ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {checkoutLoading && checkoutTier === 'qs_check' ? 'Starting checkout…' : 'Add QS Check — £199'}
        </button>
      </div>

      {/* QS Consultation Call — £199 / Pro+ redeem (Phase D) */}
      <div style={{ marginTop: 18 }} data-testid="scope-qs-call-wrap">
        <QSCallCTA variant="card" showRedeem testid="qs-call-cta-scope" userEmail={userEmail} />
      </div>

      <p style={{ fontSize: 11, color: '#64748B', textAlign: 'center', marginTop: 20, lineHeight: 1.5 }} data-testid="purchase-footer">
        Delivered as PDF within 2 minutes to <strong>{userEmail || 'your account email'}</strong>. One-off payment, no subscription. Documents available in your portal for 180 days.
      </p>
    </div>
  );
};

